import styled from '@emotion/styled';
import type { PropsOf } from '@emotion/react';
import { CloseAction } from '@components/Actions/CloseAction/CloseAction';
import { Icon } from '@components/Icon/Icon';
import { Text } from '@components/Text/Text';
import { Button } from '@components/Button/Button';
import type { IconName } from '@components/Icon/BaseIcon';
import * as tokens from '@tokens';
import type { ReactNode } from 'react';
import { useInterpolate } from '@hooks/useInterpolate';
import { useStrings } from '@hooks/useStrings';

export interface BasePopoverContentProps {
  /**
   * The title of the popover
   */
  title?: ReactNode;

  /**
   * The content section.
   */
  children?: ReactNode;

  /**
   * Function to execute when the popover closes.
   */
  onDismiss: () => void;

  /**
   * If set, it will display the corresponding icon.
   */
  icon?: IconName;

  /**
   * The text that is displayed in the action button.
   * If not defined, no action button will be displayed.
   */
  actionText?: string;

  /**
   * Function to be executed when the action button is pressed.
   */
  onActionPress?: PropsOf<'button'>['onClick'];

  /**
   * Specifies if the close button should be displayed.
   * Defaults to true.
   */
  withCloseButton?: boolean;
}

export interface PopoverContentProps
  extends Omit<BasePopoverContentProps, 'title' | 'children' | 'onDismiss'> {
  /**
   * The title of the popover
   */
  title: string;

  /**
   * The text to be displayed in the content section.
   */
  children: string;
}

const Container = styled.div`
  display: flex;
`;

const IconWrapper = styled.div`
  display: block;
  margin-right: ${tokens.spacer.x1};
  margin-top: 2px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Title = styled(Text)`
  margin-top: 2px;
  margin-right: ${tokens.spacer.x0};
`;

const Content = styled(Text)`
  width: 100%;
  margin: ${tokens.spacer.x0} ${tokens.spacer.x0} ${tokens.spacer.x1} 0;
`;

/**
 * PopoverContent component.
 *
 * **WARNING!** _This component is for internal use in the Design System. If you
 * need to use it for a custom component, please discuss your use case with
 * the Design System squad before proceeding, as they may be able to help
 * you with a better solution._
 * @summary Renders the content of a Popover.
 * @private
 */
export const PopoverContent = ({
  title,
  icon,
  children,
  onDismiss,
  actionText,
  onActionPress,
  withCloseButton = true,
}: BasePopoverContentProps): JSX.Element => {
  const { popover } = useStrings();
  const interpolate = useInterpolate();

  return (
    <Container>
      {icon && (
        <IconWrapper>
          <Icon icon={icon} />
        </IconWrapper>
      )}
      <ContentWrapper>
        <Title as="p" textType="subtitle2">
          {title}
        </Title>
        <Content as={typeof children === 'string' ? 'p' : 'span'}>
          {children}
        </Content>
        {actionText && (
          <Button kind="secondary" size="s" onClick={onActionPress}>
            {actionText}
          </Button>
        )}
      </ContentWrapper>
      {withCloseButton && (
        <CloseAction
          onClick={onDismiss}
          description={interpolate(popover.closeActionDescription, {
            // Need to ensure this is rendered as a string
            title: `${title}`,
          })}
        />
      )}
    </Container>
  );
};

PopoverContent.displayName = 'PopoverContent';
